import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { X, Send, Loader2, Check } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const absUrl = (u) => (!u ? "" : (u.startsWith("http") ? u : `${BACKEND}${u}`));

export default function ShareSheet({ reel, onClose }) {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(null); // friend id currently sending
  const [sentTo, setSentTo] = useState({}); // {friendId: true}
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/friends");
        setFriends(data);
      } catch (e) { setError(formatApiError(e)); }
      setLoading(false);
    })();
  }, []);

  const send = async (friend) => {
    setSending(friend.id);
    setError("");
    try {
      await api.post(`/reels/${reel.id}/share`, {
        friend_id: friend.id,
        note: note.trim() || null,
      });
      setSentTo((s) => ({ ...s, [friend.id]: true }));
    } catch (e) {
      setError(formatApiError(e));
    }
    setSending(null);
  };

  return (
    <div
      className="fixed inset-0 z-[85] bg-black/60 flex items-end md:items-center justify-center p-0 md:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="reel-share-sheet"
    >
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 26, stiffness: 260 }}
        className="w-full md:max-w-md bg-[#F9F8F6] text-[#0A0A0A] border-t-[3px] md:border-hard md:shadow-hard-lg border-[#0A0A0A] md:rounded-3xl rounded-t-3xl max-h-[85vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="p-4 md:p-5">
          <div className="w-12 h-1.5 bg-[#0A0A0A] rounded-full mx-auto mb-3 md:hidden" />
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase font-black tracking-widest text-[#555]">Snap&apos;i paylaş</div>
              <h2 className="font-display text-2xl leading-none">Bir arkadaşa gönder</h2>
            </div>
            <button
              onClick={onClose}
              data-testid="share-close"
              className="w-10 h-10 border-hard-2 rounded-xl flex items-center justify-center bg-white press-btn shadow-hard-sm"
              aria-label="Kapat"
            >
              <X strokeWidth={3} className="w-4 h-4" />
            </button>
          </div>

          <div className="bg-white border-hard-2 rounded-2xl p-3 mb-4 flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl border-hard-2 bg-black overflow-hidden shrink-0">
              {reel.media_type === "video" ? (
                <video src={absUrl(reel.media_url)} className="w-full h-full object-cover" muted playsInline />
              ) : (
                <img src={absUrl(reel.media_url)} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase font-black tracking-widest text-[#555]">{reel.author?.name}</div>
              <div className="font-black text-sm truncate">{reel.caption || "Snap"}</div>
            </div>
          </div>

          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={280}
            placeholder="Kısa bir not ekle (opsiyonel)…"
            data-testid="share-note-input"
            className="w-full bg-white border-hard rounded-xl px-3 py-2 mb-3 resize-none focus:outline-none text-[16px]"
          />

          {error && <div className="mb-3 border-hard-2 bg-[#FFD0C2] rounded-lg px-2 py-1 text-xs font-bold" data-testid="share-error">{error}</div>}

          {loading ? (
            <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : friends.length === 0 ? (
            <div className="border-hard-2 bg-white rounded-2xl p-4 text-center" data-testid="share-empty">
              <div className="font-black">Henüz arkadaşın yok</div>
              <div className="text-xs text-[#555]">Önce arkadaş ekle</div>
            </div>
          ) : (
            <div className="grid gap-2" data-testid="share-friend-list">
              {friends.map((f) => (
                <button
                  key={f.id}
                  onClick={() => !sentTo[f.id] && send(f)}
                  data-testid={`share-to-${f.id}`}
                  disabled={sending === f.id || sentTo[f.id]}
                  className={`flex items-center gap-3 border-hard-2 rounded-2xl p-3 press-btn shadow-hard-sm text-left ${sentTo[f.id] ? "bg-[#CCFF00]" : "bg-white"}`}
                >
                  <div className="w-11 h-11 rounded-full border-hard-2 bg-[#CCFF00] flex items-center justify-center overflow-hidden font-display shrink-0">
                    {f.picture ? <img src={absUrl(f.picture)} alt="" className="w-full h-full object-cover" /> : (f.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-sm">{f.name}</div>
                    <div className="text-xs text-[#555] truncate">{f.email}</div>
                  </div>
                  {sentTo[f.id] ? (
                    <div className="text-[10px] uppercase font-black tracking-widest bg-[#0A0A0A] text-white border-hard-2 rounded-full px-2 py-1 flex items-center gap-1">
                      <Check strokeWidth={3} className="w-3 h-3" /> Gönderildi
                    </div>
                  ) : sending === f.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <div className="w-9 h-9 rounded-xl border-hard-2 flex items-center justify-center bg-[#CCFF00]">
                      <Send strokeWidth={3} className="w-4 h-4" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
